import parser from 'any-date-parser';

const FLOATING_POINT_PRECISION = 2;
const CENTS_PER_RAND = 100;
export const SAST = 'Africa/Johannesburg';

export const displayCurrency = (amount?: number) => {
  if (amount == null || amount === undefined) {
    return '';
  }

  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount / 100);
};

// with no decimals
export const displayCurrencyNoDecimals = (amount?: number) => {
  if (amount == null) {
    return '';
  }

  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount / 100);
};

/** @deprecated Use displayCurrency instead. If you are converting from string you need to convert to number before calling currency */
export const currency = (amount: any, modifier = 100) => {
  if (amount == null || amount === undefined) {
    return '';
  }

  // This extracts the first number from the input string, it also returns an absolute value.
  const amountReg = amount.toString().match(/[\d.]+/);
  if (amountReg) {
    amount = amountReg[0];
  } else {
    return '';
  }

  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount / modifier);

  return formatted;
};

// Will display the date and time in your local timezone.
export const formatDateTimeShort = (value?: ConstructorParameters<typeof Date>[0]): string => {
  if (value == null) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  const formatter = new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  // Get the individual date components (day, month, year)
  // so we can control their order when required.
  const parts = formatter.formatToParts(date);

  const p = Object.fromEntries(parts.map(({ type, value }) => [type, value]));

  const locale = formatter.resolvedOptions().locale;

  // For South Africa explicitly use DD/MM/YYYY h:mma.
  if (locale.startsWith('en-ZA')) {
    return `${p.day}/${p.month}/${p.year}, ${p.hour}:${p.minute}${p.dayPeriod.toLowerCase()}`;
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
};

// Will always display the south african date because there is no time component present.
export const formatDateMedium = (value?: ConstructorParameters<typeof Date>[0]): string => {
  if (value == null) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: SAST }).format(new Date(value));
};

/**
 * Formats a date using the browser's locale.
 * Will always display the south african date because there is no time component present.
 *
 * @param value - Date value as a string
 * @returns Formatted date string, or an empty string for an invalid/missing date.
 */
export const formatDateShort = (value?: ConstructorParameters<typeof Date>[0]): string => {
  if (value == null) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  const formatter = new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: SAST,
  });

  // Get the individual date components (day, month, year)
  // so we can control their order when required.
  const parts = formatter.formatToParts(date);

  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));

  const locale = formatter.resolvedOptions().locale;

  // For South Africa explicitly use DD/MM/YYYY.
  if (locale.startsWith('en-ZA')) {
    return `${values.day}/${values.month}/${values.year}`;
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeZone: SAST }).format(new Date(value));
};

export const toCents = (amount: number): number => {
  return Math.round(Number((amount * CENTS_PER_RAND).toFixed(FLOATING_POINT_PRECISION)));
};

export const toRand = (amount: number): number => {
  if (!amount) {
    return 0;
  }
  return amount / CENTS_PER_RAND;
};

export const toRandString = (amount: number): string => {
  if (!amount) {
    return '0.00';
  }
  return (amount / CENTS_PER_RAND).toFixed(2);
};

export const formatNameForAvs = (names: string) => {
  const splitNames = names.trim().split(' ');
  const initials = splitNames.map((str) => str.charAt(0)).join('');
  const surname = splitNames[splitNames.length - 1];
  return { initials, surname };
};

// Some order notifications have 'medium' format dates, which we parse using any-date-parser
export const parseOldNotificationDates = (value?: string) => {
  // undefined parameters need to be ignored
  if (value == null) return value;
  // Attempt to use Date parser for standard ISOStrings
  const parsedDate = new Date(value);
  if (!Number.isNaN(parsedDate.getTime())) return parsedDate;
  // Any other strings need to be parsed and assumed that they were created in UTC+2 timezones
  const p = parser.attempt(value);

  if (p.invalid || p.year == null || p.month == null || p.day == null) {
    throw new Error(`Unable to parse date: ${value}`);
  }

  const wallClock = Date.UTC(p.year, p.month - 1, p.day, p.hour ?? 0, p.minute ?? 0, p.second ?? 0, p.millisecond ?? 0);

  // If an explicit timezone offset was parsed, use it.
  // Otherwise, assume UTC+02:00.
  const offsetMinutes = p.offset ?? 120;

  return new Date(wallClock - offsetMinutes * 60_000);
};
